import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { convertBase64ToFile } from '../../utils/maks'
import { useTranslation } from 'react-i18next'

enum Steps {
    TakePhoto = 1,
    ConfirmPhoto = 2,
}

interface Props {
    goBack(): void
    returnPhoto(file: File, base64: string): void
}

export const Camera = ({ goBack, returnPhoto }: Props) => {
    const { t } = useTranslation()
    const [step, setStep] = useState(Steps.TakePhoto)
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const stream = useRef<MediaStream>()

    const startCamera = async () => {
        if (!videoRef.current) return

        stream.current = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: 'environment',
            },
        })

        videoRef.current.srcObject = stream.current
        videoRef.current.play()
    }

    const stopCamera = () => {
        if (videoRef.current) {
            videoRef.current.srcObject = null
            videoRef.current.pause()
        }

        stream.current &&
            stream.current.getTracks().forEach((track) => track.stop())
    }

    useEffect(() => {
        setTimeout(() => {
            startCamera()
        }, 1000)

        return stopCamera
    }, [])

    const handleTakePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return

        canvasRef.current.width = videoRef.current.videoWidth
        canvasRef.current.height = videoRef.current.videoHeight
        const ctx = canvasRef.current.getContext('2d')
        ctx?.scale(-1, 1)
        ctx?.drawImage(videoRef.current, canvasRef.current.width * -1, 0)

        setStep(Steps.ConfirmPhoto)
    }

    const handleConfirmPhoto = async () => {
        if (!canvasRef.current) return

        const base64 = canvasRef.current.toDataURL()
        const file = await convertBase64ToFile(base64)

        stopCamera()
        returnPhoto(file, base64)
        goBack()
    }

    return (
        <>
            <canvas
                ref={canvasRef}
                style={{
                    display: step === Steps.ConfirmPhoto ? 'block' : 'none',
                    width: '100%',
                }}
            />
            <video
                ref={videoRef}
                style={{
                    width: '100%',
                    objectFit: 'fill',
                    transform: 'rotateY(180deg)',
                    display: step === Steps.TakePhoto ? 'block' : 'none',
                }}
            />
            <Button
                variant="contained"
                color="primary"
                sx={{
                    width: '100%',
                    textTransform: 'none',
                    fontSize: '16px',
                    py: '12px',
                    display: 'flex',
                    mt: '5px',
                }}
                onClick={() => {
                    if (step === Steps.TakePhoto) handleTakePhoto()
                    else handleConfirmPhoto()
                }}
            >
                {step === Steps.TakePhoto ? t('Capturar') : t('Confirmar')}
            </Button>
            <Button
                variant="contained"
                color="secondary"
                sx={{
                    width: '100%',
                    textTransform: 'none',
                    fontSize: '16px',
                    py: '12px',
                    display: 'flex',
                    mt: '15px',
                }}
                onClick={() => {
                    if (step === Steps.TakePhoto) {
                        stopCamera()
                        goBack()
                    } else setStep(Steps.TakePhoto)
                }}
            >
                {t('Volver')}
            </Button>
        </>
    )
}
